const pad2 = (value: number) => String(value).padStart(2, '0');
const UK_LOCALE = 'en-GB';
const ISO_DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_DATE_TIME_PREFIX_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:T|\s)/;

const UK_DATE_FORMATTER = new Intl.DateTimeFormat(UK_LOCALE, {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric'
});

const UK_DATE_TIME_FORMATTER = new Intl.DateTimeFormat(UK_LOCALE, {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false
});

const UK_TIME_FORMATTER = new Intl.DateTimeFormat(UK_LOCALE, {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false
});

const buildLocalDate = (yearStr: string, monthStr: string, dayStr: string) => {
  const year = Number(yearStr);
  const monthIndex = Number(monthStr) - 1;
  const day = Number(dayStr);
  const parsed = new Date(year, monthIndex, day);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const parseDateValue = (
  value?: string | Date | number | null,
  options: { useDatePrefix?: boolean } = {}
) => {
  if (value == null || value === '') return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number') {
    const fromNumber = new Date(value);
    return Number.isNaN(fromNumber.getTime()) ? null : fromNumber;
  }

  const text = String(value).trim();
  if (!text) return null;

  const fullDateMatch = text.match(ISO_DATE_ONLY_PATTERN);
  if (fullDateMatch) {
    return buildLocalDate(fullDateMatch[1], fullDateMatch[2], fullDateMatch[3]);
  }

  if (options.useDatePrefix) {
    const datePrefixMatch = text.match(ISO_DATE_TIME_PREFIX_PATTERN);
    if (datePrefixMatch) {
      return buildLocalDate(datePrefixMatch[1], datePrefixMatch[2], datePrefixMatch[3]);
    }
  }

  let parsed = new Date(text);
  if (Number.isNaN(parsed.getTime()) && text.includes(' ')) {
    parsed = new Date(text.replace(' ', 'T'));
  }

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const parseDateWithTime = (dateValue?: string | Date | null, timeValue?: string | null) => {
  if (!dateValue) return null;
  if (dateValue instanceof Date) {
    return Number.isNaN(dateValue.getTime()) ? null : dateValue;
  }
  if (typeof dateValue !== 'string') return null;

  const trimmed = dateValue.trim();
  if (!trimmed) return null;

  let parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime()) && timeValue) {
    parsed = new Date(`${trimmed}T${timeValue}`);
  }
  if (Number.isNaN(parsed.getTime()) && trimmed.includes(' ')) {
    parsed = new Date(trimmed.replace(' ', 'T'));
  }
  if (Number.isNaN(parsed.getTime()) && /^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
    const [day, month, year] = trimmed.split('/');
    parsed = new Date(`${year}-${month}-${day}`);
  }

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatDateOnly = (date: Date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

export const normalizeDateOnly = (value?: string | Date | null) => {
  if (!value) return '';

  if (value instanceof Date) {
    return formatDateOnly(value);
  }

  const text = String(value);
  const directMatch = text.match(/^(\d{4}-\d{2}-\d{2})$/);
  if (directMatch) {
    return directMatch[1];
  }

  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) {
    return text;
  }

  return formatDateOnly(parsed);
};

export const formatUkDate = (
  value?: string | Date | number | null,
  fallback = '',
  options: { preserveTimeZone?: boolean } = {}
) => {
  const parsed = parseDateValue(value, { useDatePrefix: !options.preserveTimeZone });
  if (!parsed) return fallback;
  return UK_DATE_FORMATTER.format(parsed);
};

export const formatUkDateTime = (value?: string | Date | number | null, fallback = '') => {
  const parsed = parseDateValue(value);
  if (!parsed) return fallback;
  return UK_DATE_TIME_FORMATTER.format(parsed);
};

export const formatUkTime = (value?: string | Date | number | null, fallback = '') => {
  const parsed = parseDateValue(value);
  if (!parsed) return fallback;
  return UK_TIME_FORMATTER.format(parsed);
};
